import { COLORS } from "../shared/constants";
import { IconBack, IconAlert } from "../shared/icons";
import { useState, useEffect, useRef } from "react";
import * as api from "../api";

export default // ─── MT Determination Screen ─────────────────────────────────
// Dedicated Motor Threshold determination with device control.
// Launched from Treatment Session Screen with patient context.
// Auto-loads safety protocol to MagPro on entry.
// Returns MT value to session via onSaveMT callback.
function MTDeterminationScreen({ patient, treatment, protocol, onSaveMT, onBack, coils, mtSpots, dueReminders, assessmentScales, onOpenAssessment, deviceSessionId, returnedTxRatio, onTreatmentUpdated }) {
  const [connected, setConnected] = useState(false);
  const [simulated, setSimulated] = useState(false);
  const [telemetry, setTelemetry] = useState(null);
  const [mtValue, setMtValue] = useState(treatment?.last_mt_value ? String(treatment.last_mt_value) : "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [protocolLoaded, setProtocolLoaded] = useState(false);
  const [deviceArmed, setDeviceArmed] = useState(false);
  const [lastSavedMT, setLastSavedMT] = useState(treatment?.last_mt_value || null);
  const [justSaved, setJustSaved] = useState(false);
  const [mtCoil, setMtCoil] = useState(treatment?.mt_coil || protocol?.mt_coil || "");
  const [mtSpot, setMtSpot] = useState(treatment?.mt_spot || protocol?.mt_spot || "");
  // ─── Tx %MT (treatment-dose-as-percent-of-MT) ──────────
  // Default chain: just-set-but-unsaved value > persisted last value on
  // treatment > protocol default > 120. Stored as a number; the dropdown
  // shows the matching preset (120/100/80) or "Other" with an inline input
  // when the value isn't a preset.
  const TX_RATIO_PRESETS = [120, 100, 80];
  const initialTxRatio = (returnedTxRatio
    ?? treatment?.last_treatment_ratio_pct
    ?? protocol?.treatment_ratio_pct
    ?? 120);
  const [txRatioPct, setTxRatioPct] = useState(initialTxRatio);
  // "other" UI mode is true when the current value isn't one of the presets.
  // Initialized from the current ratio, then driven by dropdown changes.
  const [txRatioOther, setTxRatioOther] = useState(
    !TX_RATIO_PRESETS.includes(initialTxRatio)
  );
  const pollRef = useRef(null);

  // ─── Check connection on mount ──────────────────────────
  useEffect(() => {
    api.deviceStatus().then((s) => {
      setConnected(s.connected || false);
      setSimulated(s.simulated || false);
    }).catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // ─── Telemetry polling when connected ───────────────────
  useEffect(() => {
    if (!connected) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    const poll = () => {
      api.deviceTelemetry().then((t) => {
        setTelemetry(t);
        setDeviceArmed(t.device_enabled || false);
        setEvents(t.events || []);
      }).catch(() => {});
    };
    poll();
    pollRef.current = setInterval(poll, 1500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [connected]);

  // ─── Auto-load safety MT protocol on connect ────────────
  useEffect(() => {
    if (!connected || protocolLoaded) return;
    const loadMTProtocol = async () => {
      try {
        await api.deviceLoadMTProtocol(deviceSessionId);
        setProtocolLoaded(true);
      } catch (err) {
        console.warn("MT protocol load warning:", err);
        setProtocolLoaded(true);
      }
    };
    loadMTProtocol();
  }, [connected, protocolLoaded]);

  // ─── Always sync MT value from device amplitude ──────────
  useEffect(() => {
    if (!connected || !telemetry?.amplitude_pct) return;
    if (telemetry.amplitude_pct > 0) {
      const newVal = String(telemetry.amplitude_pct);
      setMtValue((prev) => {
        if (prev !== newVal) {
          // Amplitude changed — if we just saved and it's different now, clear justSaved
          setJustSaved((wasSaved) => {
            if (wasSaved && newVal !== String(lastSavedMT)) return false;
            return wasSaved;
          });
        }
        return newVal;
      });
    }
  }, [connected, telemetry?.amplitude_pct, lastSavedMT]);

  // ─── Handlers ───────────────────────────────────────────
  const handleConnect = async () => {
    setLoading(true); setError(null);
    try {
      const result = await api.deviceConnect();
      setConnected(result.success);
      setSimulated(result.simulated || false);
      if (!result.success) setError(result.message);
    } catch (err) { setError(err.error || "Connection failed"); }
    setLoading(false);
  };

  const handleAmplitudeChange = async (newVal) => {
    const v = Math.max(1, Math.min(100, newVal));
    setMtValue(String(v));
    if (v !== lastSavedMT) setJustSaved(false);
    if (connected) {
      try {
        await api.deviceSetAmplitude(v, deviceSessionId);
      } catch (err) { console.warn("Set amplitude:", err); }
    }
  };

  const handleFire = async () => {
    setError(null);
    try {
      if (!deviceArmed) {
        await api.deviceArm(deviceSessionId);
        setDeviceArmed(true);
      }
      await api.deviceFire(deviceSessionId);
    } catch (err) {
      setError(err.error || "Fire failed");
    }
  };

  const handleSaveMT = async () => {
    if (!mtValue || parseInt(mtValue) < 1) return;
    const val = parseInt(mtValue);
    // Validate ratio — fall back to safe default if user left "Other" blank
    // or entered something invalid.
    const safeRatio = (Number.isFinite(txRatioPct) && txRatioPct >= 1 && txRatioPct <= 200)
      ? txRatioPct
      : (treatment?.last_treatment_ratio_pct || protocol?.treatment_ratio_pct || 120);
    // Persist to DB so the measurement survives if the operator navigates
    // away before logging a session. Best-effort: log a warning but don't
    // block the screen-return if the network call fails — App.js state
    // still carries the values forward in-memory for the current flow.
    if (treatment?.id) {
      try {
        const resp = await api.saveMT(treatment.id, {
          lastMtValue: val,
          lastTreatmentRatioPct: safeRatio,
          mtCoil: mtCoil || null,
          mtSpot: mtSpot || null,
        });
        // Push the freshly-saved row up so App.js's treatments[] cache
        // doesn't go stale. Without this, navigating away from the
        // treatment screen and back would show the previous MT value
        // even though the DB has the new one.
        if (resp?.treatment && typeof onTreatmentUpdated === "function") {
          onTreatmentUpdated(resp.treatment);
        }
      } catch (err) {
        console.warn("saveMT persistence failed (continuing in-memory):", err);
      }
    }
    setLastSavedMT(val);
    setJustSaved(true);
    onSaveMT(val, mtCoil, mtSpot, safeRatio);
  };

  const tempColor = telemetry?.coil_temp_status === "red" ? "#E74C3C"
    : telemetry?.coil_temp_status === "yellow" ? "#F39C12" : "#2ECC71";

  return (
    <div style={{ padding: "24px", maxWidth: "900px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button onClick={onBack} style={{ background: "none", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "8px", cursor: "pointer", display: "flex", alignItems: "center", color: COLORS.textSecondary }}><IconBack /></button>
          <div>
            <h2 style={{ color: COLORS.text, fontSize: "20px", fontWeight: "600", margin: 0 }}>Motor Threshold Determination</h2>
            <div style={{ fontSize: "13px", color: COLORS.textSecondary, marginTop: "2px" }}>
              {patient.first_name} {patient.last_name} · {treatment.protocol_name}
            </div>
          </div>
        </div>
        <span style={{ padding: "4px 12px", borderRadius: "8px", fontSize: "12px", fontWeight: "600",
          background: COLORS.primaryLight, color: COLORS.primary }}>
          MT Determination
        </span>
      </div>

      {/* Precautions */}
      {patient.precautions && (
        <div style={{ background: COLORS.dangerLight, border: `1px solid ${COLORS.dangerBorder}`, borderRadius: "10px", padding: "12px 16px", marginBottom: "12px", display: "flex", alignItems: "flex-start", gap: "10px" }}>
          <span style={{ color: COLORS.danger, flexShrink: 0, marginTop: "1px" }}><IconAlert /></span>
          <div>
            <span style={{ fontSize: "12px", fontWeight: "700", color: COLORS.danger, textTransform: "uppercase" }}>Precautions</span>
            <p style={{ margin: "4px 0 0", fontSize: "14px", color: COLORS.text }}>{patient.precautions}</p>
          </div>
        </div>
      )}

      {error && (
        <div style={{ padding: "12px 16px", background: COLORS.dangerLight, border: `1px solid ${COLORS.dangerBorder}`, borderRadius: "10px", marginBottom: "16px", fontSize: "13px", color: COLORS.danger }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: "12px", background: "none", border: "none", color: COLORS.danger, cursor: "pointer", fontWeight: "600" }}>✕</button>
        </div>
      )}

      {/* Due Reminders — same as Treatment Session Screen */}
      {dueReminders && dueReminders.length > 0 && (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "20px" }}>
          {dueReminders.map((rem) => {
            const colorMap = {
              yellow: { bg: "#FFF9E6", border: "#F0D060", text: "#8B7000", icon: "⚡" },
              blue: { bg: "#E8F0FE", border: "#7BAAF7", text: "#1A56DB", icon: "📋" },
              orange: { bg: "#FFF3E6", border: "#F0A050", text: "#9A5B00", icon: "🔔" },
            };
            const c = colorMap[rem.color] || colorMap.orange;
            const isAssessment = rem.reminder_type === "ASSESSMENT";
            const isMT = rem.reminder_type === "MT_REDETERMINATION";
            return (
              <div key={rem.id}
                style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: "8px", padding: "8px 14px", display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ fontSize: "14px" }}>{c.icon}</span>
                <span style={{ fontSize: "12px", fontWeight: "700", color: c.text }}>
                  {isMT ? "MT Redetermination Due" : isAssessment ? `${rem.label} Due` : rem.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
        {/* ─── Left: MT Controls ──────────────── */}
        <div style={{ background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "20px" }}>
          <div style={{ background: "#E8F8F0", borderRadius: "10px", border: `1px solid #C8E6D9`, padding: "20px", marginBottom: "16px", minHeight: "190px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            {/* Title */}
            <div style={{ textAlign: "center", fontSize: "13px", fontWeight: "700", color: COLORS.accent, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Adjust Amplitude
            </div>

            {/* +/- Amplitude */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "16px", padding: "12px 0" }}>
              <button onClick={() => handleAmplitudeChange((parseInt(mtValue) || 0) - 1)}
                style={{ width: "48px", height: "48px", borderRadius: "12px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: "28px", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
                −
              </button>
              <span style={{ fontSize: "56px", fontWeight: "800", color: COLORS.text, minWidth: "120px", textAlign: "center", display: "inline-flex", alignItems: "baseline", justifyContent: "center" }}>
                <input type="text" inputMode="numeric" pattern="[0-9]*"
                  value={mtValue}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9]/g, "");
                    if (v === "" || (parseInt(v) >= 0 && parseInt(v) <= 100)) setMtValue(v);
                  }}
                  onBlur={() => {
                    if (connected && mtValue && parseInt(mtValue) > 0) {
                      api.deviceSetAmplitude(parseInt(mtValue), deviceSessionId).catch(() => {});
                    }
                  }}
                  style={{ width: `${Math.max(2, String(mtValue || "—").length + 1) * 0.55}em`, fontSize: "inherit", fontWeight: "inherit", lineHeight: 1, textAlign: "right", color: "inherit", background: "transparent", border: "none", outline: "none", padding: 0, margin: 0 }}
                  placeholder="—" />%
              </span>
              <button onClick={() => handleAmplitudeChange((parseInt(mtValue) || 0) + 1)}
                style={{ width: "48px", height: "48px", borderRadius: "12px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: "28px", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
                +
              </button>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "16px" }}>
              <span style={{ fontSize: "13px", color: COLORS.textSecondary }}>
                {connected && telemetry?.amplitude_pct != null
                  ? `Device: ${telemetry.amplitude_pct}%`
                  : "Enter MT value"}
              </span>
              {connected && (
                <span style={{ fontSize: "11px", color: deviceArmed ? COLORS.accent : COLORS.textSecondary }}>
                  {deviceArmed ? "● Ready to fire" : "○ Arms before firing"}
                </span>
              )}
            </div>
          </div>

          {/* MT Coil, MT Spot, Tx %MT dropdowns */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", marginBottom: "8px" }}>
            <div>
              <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "4px" }}>MT Coil</label>
              <select value={mtCoil} onChange={(e) => setMtCoil(e.target.value)}
                style={{ width: "100%", height: "36px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "13px", color: COLORS.text, background: COLORS.surface }}>
                <option value="">— Select —</option>
                {(coils || []).filter(c => c.is_active !== false).map(c => (
                  <option key={c.id || c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "4px" }}>MT Spot</label>
              <select value={mtSpot} onChange={(e) => setMtSpot(e.target.value)}
                style={{ width: "100%", height: "36px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "13px", color: COLORS.text, background: COLORS.surface }}>
                <option value="">— Select —</option>
                {(mtSpots || []).filter(s => s.is_active !== false).map(s => (
                  <option key={s.id || s.name} value={s.name}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "4px" }}>Tx %MT</label>
              <select
                value={txRatioOther ? "other" : String(txRatioPct)}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "other") {
                    // Switch to custom input mode; keep current value as
                    // starting point (user can edit it).
                    setTxRatioOther(true);
                  } else {
                    setTxRatioOther(false);
                    setTxRatioPct(parseInt(v));
                  }
                }}
                style={{ width: "100%", height: "36px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "13px", color: COLORS.text, background: COLORS.surface }}>
                {TX_RATIO_PRESETS.map(r => (
                  <option key={r} value={String(r)}>{r}%</option>
                ))}
                <option value="other">Other…</option>
              </select>
            </div>
          </div>

          {/* Tx %MT custom input — appears only when "Other" is selected */}
          {txRatioOther && (
            <div style={{ marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
              <label style={{ fontSize: "12px", color: COLORS.textSecondary, whiteSpace: "nowrap" }}>
                Custom Tx %MT:
              </label>
              <input
                type="number"
                inputMode="numeric"
                min="1"
                max="200"
                value={Number.isFinite(txRatioPct) ? String(txRatioPct) : ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") { setTxRatioPct(NaN); return; }
                  const n = parseInt(raw);
                  if (!Number.isNaN(n)) setTxRatioPct(Math.max(1, Math.min(200, n)));
                }}
                style={{ width: "80px", height: "32px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "13px", textAlign: "center" }}
              />
              <span style={{ fontSize: "12px", color: COLORS.textSecondary }}>%</span>
              {mtValue && parseInt(mtValue) > 0 && Number.isFinite(txRatioPct) && (
                <span style={{ fontSize: "12px", color: COLORS.textSecondary, marginLeft: "auto" }}>
                  → Tx dose: {Math.min(100, Math.round((parseInt(mtValue) * txRatioPct) / 100))}%
                </span>
              )}
            </div>
          )}

          {/* Spacer below dropdowns */}
          <div style={{ marginBottom: "4px" }} />

          {/* Save MT button */}
          {justSaved ? (
            <div>
              <button disabled
                style={{
                  width: "100%", height: "52px", borderRadius: "12px", border: "none",
                  background: "#2ECC71", color: "#FFF", fontSize: "15px", fontWeight: "700",
                  cursor: "default", marginBottom: "8px",
                }}>
                ✓ Saved MT ({lastSavedMT}%)
              </button>
              <button onClick={onBack}
                style={{
                  width: "100%", height: "48px", borderRadius: "12px", border: "none",
                  background: "#F39C12", color: "#FFF", fontSize: "14px", fontWeight: "700",
                  cursor: "pointer",
                }}>
                Go Back to Treatment Session
              </button>
            </div>
          ) : (
            <div>
              <button onClick={handleSaveMT} disabled={!mtValue || parseInt(mtValue) < 1}
                style={{
                  width: "100%", height: "52px", borderRadius: "12px", border: "none",
                  background: mtValue && parseInt(mtValue) >= 1 ? COLORS.accent : COLORS.border,
                  color: "#FFF", fontSize: "15px", fontWeight: "700",
                  cursor: mtValue && parseInt(mtValue) >= 1 ? "pointer" : "default",
                  opacity: mtValue && parseInt(mtValue) >= 1 ? 1 : 0.4,
                  marginBottom: "8px",
                }}>
                Save MT {lastSavedMT ? `(${lastSavedMT}%)` : "(—)"}
              </button>
              {lastSavedMT && (
                <button onClick={onBack}
                  style={{
                    width: "100%", height: "48px", borderRadius: "12px", border: "none",
                    background: "#F39C12", color: "#FFF", fontSize: "14px", fontWeight: "700",
                    cursor: "pointer",
                  }}>
                  Go Back to Treatment Session
                </button>
              )}
            </div>
          )}
        </div>

        {/* ─── Right: Telemetry + Connection + Events ──────── */}
        <div>
          {/* Telemetry */}
          <div style={{ background: "#1A1F2E", borderRadius: "12px", padding: "20px", marginBottom: "16px", color: "rgba(255,255,255,0.5)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <div style={{ fontSize: "12px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Telemetry</div>
              {connected && (
                <span style={{ fontSize: "10px", padding: "2px 8px", borderRadius: "4px",
                  background: simulated ? "rgba(52,152,219,0.2)" : "rgba(46,204,113,0.2)",
                  color: simulated ? "#3498DB" : "#2ECC71" }}>
                  {simulated ? "Simulated" : "Live"}
                </span>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
              <div style={{ padding: "12px", background: "rgba(255,255,255,0.05)", borderRadius: "8px",
                borderLeft: telemetry?.coil_temp_status === "red" ? "3px solid #E74C3C" : telemetry?.coil_temp_status === "yellow" ? "3px solid #F39C12" : "3px solid transparent" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", marginBottom: "4px" }}>Coil Temp</div>
                <div style={{ fontSize: "24px", fontWeight: "700",
                  color: !connected ? "rgba(255,255,255,0.3)"
                    : telemetry?.no_coil_flow ? "#3498DB"
                    : telemetry?.coil_temp_status === "red" ? "#E74C3C"
                    : telemetry?.coil_temp_status === "yellow" ? "#F39C12"
                    : "#2ECC71" }}>
                  {connected && telemetry
                    ? (telemetry.no_coil_flow ? "No Flow" : telemetry.coil_temp_c != null ? `${telemetry.coil_temp_c}°C` : "--°C")
                    : "--°C"}
                </div>
              </div>
              <div style={{ padding: "12px", background: "rgba(255,255,255,0.05)", borderRadius: "8px" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", marginBottom: "4px" }}>Amplitude</div>
                <div style={{ fontSize: "24px", fontWeight: "700", color: connected && telemetry ? "#FFF" : "rgba(255,255,255,0.3)" }}>
                  {connected && telemetry?.amplitude_pct != null ? `${telemetry.amplitude_pct}%` : "--%"}
                </div>
              </div>
              <div style={{ padding: "12px", background: "rgba(255,255,255,0.05)", borderRadius: "8px" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", marginBottom: "4px" }}>Status</div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <div style={{ width: "8px", height: "8px", borderRadius: "50%",
                    background: connected ? (telemetry?.device_enabled ? "#2ECC71" : "#3498DB") : "#E74C3C" }} />
                  <span style={{ fontSize: "13px", color: "rgba(255,255,255,0.7)" }}>
                    {connected ? (telemetry?.device_enabled ? "Enabled" : "Connected") : "Offline"}
                  </span>
                </div>
              </div>
              <div style={{ padding: "12px", background: "rgba(255,255,255,0.05)", borderRadius: "8px",
                borderLeft: "3px solid transparent" }}>
                <div style={{ fontSize: "10px", textTransform: "uppercase", marginBottom: "4px" }}>Coil Type</div>
                <div style={{ fontSize: "13px", color: connected && telemetry?.coil_type ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.3)" }}>
                  {connected && telemetry?.coil_type ? telemetry.coil_type : (treatment.treatment_coil || protocol?.treatment_coil || "—")}
                </div>
              </div>
            </div>

            {connected && telemetry && (
              <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.4)" }}>
                {telemetry.mode && <span>Mode: {telemetry.mode} · </span>}
                {telemetry.waveform && <span>Waveform: {telemetry.waveform}</span>}
              </div>
            )}

            {/* Connect button — only when disconnected */}
            {!connected && (
              <div style={{ marginTop: "12px" }}>
                <button onClick={handleConnect} disabled={loading}
                  style={{ width: "100%", height: "40px", borderRadius: "8px", border: "none", background: COLORS.primary, color: "#FFF", fontSize: "13px", fontWeight: "700", cursor: loading ? "wait" : "pointer" }}>
                  {loading ? "Connecting…" : "Connect to MagPro"}
                </button>
              </div>
            )}
          </div>

          {/* Event Log */}
          <div style={{ background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "20px" }}>
            <div style={{ fontSize: "12px", fontWeight: "700", color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "12px" }}>Event Log</div>
            <div style={{ maxHeight: "200px", overflowY: "auto" }}>
              {events.length === 0 ? (
                <div style={{ fontSize: "13px", color: COLORS.textSecondary, opacity: 0.5 }}>No events yet</div>
              ) : (
                events.slice().reverse().map((evt, i) => (
                  <div key={i} style={{ fontSize: "12px", color: COLORS.textSecondary, padding: "4px 0", borderBottom: `1px solid ${COLORS.borderLight}`, fontFamily: "monospace" }}>
                    <span style={{ color: COLORS.text, opacity: 0.4 }}>{evt.timestamp?.split("T")[1]?.substring(0, 8)}</span>{" "}
                    {evt.message}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
